import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Button } from "./button";
import { MessageCircle, CheckSquare, Lightbulb, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";

interface FieldActionMenuProps {
  children: React.ReactNode;
  fieldLabel: string;
  fieldValue?: string;
  onQuote?: () => void;
  onCopyToAction?: (content: string) => void;
  onCopyToInsight?: (content: string) => void;
  disabled?: boolean;
}

/**
 * FieldActionMenu Component
 *
 * Provides a visible dropdown menu for form fields with quick actions:
 * - Quote this field in chat
 * - Add field content as Action Item
 * - Add field content as Insight
 *
 * @param children - The form field component to wrap
 * @param fieldLabel - Human-readable label of the field
 * @param fieldValue - Current value/content of the field
 * @param onQuote - Callback to quote field in chat
 * @param onCopyToAction - Callback to create Action Item from field content
 * @param onCopyToInsight - Callback to create Insight from field content
 * @param disabled - Disable the menu (e.g., in review mode)
 */
export function FieldActionMenu({
  children,
  fieldLabel,
  fieldValue = "",
  onQuote,
  onCopyToAction,
  onCopyToInsight,
  disabled = false,
}: FieldActionMenuProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  if (disabled) {
    return <>{children}</>;
  }

  // Don't show menu if no actions are available
  if (!onQuote && !onCopyToAction && !onCopyToInsight) {
    return <>{children}</>;
  }

  return (
    <div className="relative group">
      {children}

      {/* Menu button - always visible, positioned based on text direction */}
      <div className={`absolute top-1 z-10 ${isRTL ? 'left-1' : 'right-1'}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0 opacity-60 hover:opacity-100 hover:bg-primary/10 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? "start" : "end"} className="w-56">
            {onQuote && (
              <DropdownMenuItem onClick={onQuote} className="cursor-pointer">
                <MessageCircle className="me-2 h-4 w-4 flex-shrink-0 text-primary" />
                <div className="flex flex-col overflow-hidden">
                  <span className="font-medium text-sm">
                    {t("quote_field_in_chat") || "Quote in chat"}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {fieldLabel}
                  </span>
                </div>
              </DropdownMenuItem>
            )}

            {onCopyToAction && (
              <DropdownMenuItem
                onClick={() => onCopyToAction(fieldValue)}
                className="cursor-pointer"
                disabled={!fieldValue.trim()}
              >
                <CheckSquare className="me-2 h-4 w-4 flex-shrink-0 text-primary" />
                <div className="flex flex-col overflow-hidden">
                  <span className="font-medium text-sm">
                    {t("add_as_action_item") || "Add as Action Item"}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {fieldValue.trim() ? fieldLabel : t("field_is_empty") || "Field is empty"}
                  </span>
                </div>
              </DropdownMenuItem>
            )}

            {onCopyToInsight && (
              <DropdownMenuItem
                onClick={() => onCopyToInsight(fieldValue)}
                className="cursor-pointer"
                disabled={!fieldValue.trim()}
              >
                <Lightbulb className="me-2 h-4 w-4 flex-shrink-0 text-amber-500" />
                <div className="flex flex-col overflow-hidden">
                  <span className="font-medium text-sm">
                    {t("add_as_insight") || "Add as Insight"}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {fieldValue.trim() ? fieldLabel : t("field_is_empty") || "Field is empty"}
                  </span>
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
